/**
 * Sino e painel de notificações do menu superior (JOR4-7365).
 *
 * Só a apresentação: o socket, o histórico e o "marcar como lida" continuam no
 * HermesNotifier, que alimenta este componente.
 */

import { AlertTriangle, Bell, Check, Info, Search, X } from "lucide-react";
import moment from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import { createRoot, Root } from "react-dom/client";

import { usarFecharDropdown } from "../componentes-padrao/usarFecharDropdown";

export interface Notificacao {
    id: number;
    message: string;
    messageType: string;
    createdAt: string;
    read: boolean;
}

export interface EstadoPainelNotificacoes {
    notificacoes: Notificacao[];
    aoMarcarComoLida: (id: number) => void;
    aoMarcarTodasComoLidas: () => void;
}

const iconeDoTipo = (tipo: string) => {
    switch (tipo) {
        case "warning":
        case "error":
            return AlertTriangle;
        case "success":
            return Check;
        default:
            return Info;
    }
};

export const PainelNotificacoes = ({
    notificacoes,
    aoMarcarComoLida,
    aoMarcarTodasComoLidas,
}: EstadoPainelNotificacoes) => {
    const [aberto, setAberto] = useState(false);
    const [busca, setBusca] = useState("");
    const container = useRef<HTMLDivElement>(null);

    // RN06: o badge mostra a contagem de não lidas, não só um indicador.
    const naoLidas = notificacoes.filter((n) => !n.read).length;

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return notificacoes;
        return notificacoes.filter((n) =>
            (n.message || "").toLowerCase().includes(termo),
        );
    }, [notificacoes, busca]);

    const fechar = useCallback(() => setAberto(false), []);

    usarFecharDropdown(aberto, container, fechar);

    return (
        <div className="notificacoes-ds" ref={container}>
            <button
                type="button"
                className="topbar-ds-icone notificacoes-ds-sino"
                onClick={() => setAberto((atual) => !atual)}
                aria-label="Notificações"
                title="Notificações"
            >
                <Bell size={14} />
                {naoLidas > 0 && (
                    <span className="notificacoes-ds-badge">
                        {naoLidas > 99 ? "99+" : naoLidas}
                    </span>
                )}
            </button>

            {aberto && (
                <div className="notificacoes-ds-painel">
                    <div className="notificacoes-ds-cabecalho">
                        <h3>Notificações</h3>
                        <div className="notificacoes-ds-cabecalho-acoes">
                            <button
                                type="button"
                                className="notificacoes-ds-ler-todas"
                                onClick={aoMarcarTodasComoLidas}
                            >
                                Ler todas
                            </button>
                            <button
                                type="button"
                                className="notificacoes-ds-fechar"
                                onClick={() => setAberto(false)}
                                aria-label="Fechar"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    <div className="notificacoes-ds-busca">
                        <Search size={11} />
                        <input
                            type="text"
                            placeholder="Pesquisar ..."
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                    </div>

                    <div className="notificacoes-ds-lista">
                        {filtradas.length === 0 ? (
                            <p className="notificacoes-ds-vazio">
                                Nenhuma notificação encontrada
                            </p>
                        ) : (
                            filtradas.map((notificacao) => {
                                const Icone = iconeDoTipo(
                                    notificacao.messageType,
                                );
                                return (
                                    <div
                                        key={notificacao.id}
                                        className={`notificacoes-ds-item notificacao-${notificacao.messageType}${
                                            notificacao.read ? "" : " is-nao-lida"
                                        }`}
                                    >
                                        <span className="notificacoes-ds-icone">
                                            <Icone size={10} />
                                        </span>

                                        <div className="notificacoes-ds-texto">
                                            {/*
                                             * As mensagens do Hermes contêm
                                             * links, e já eram injetadas como
                                             * HTML pelo componente anterior.
                                             * Mantido para não perder os links;
                                             * a origem é o próprio backend.
                                             */}
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: notificacao.message,
                                                }}
                                            />
                                            <span className="notificacoes-ds-data">
                                                {moment
                                                    .utc(notificacao.createdAt)
                                                    .format(
                                                        "DD/MM/YYYY HH:mm:ss",
                                                    )}
                                            </span>
                                        </div>

                                        {!notificacao.read && (
                                            <button
                                                type="button"
                                                className="notificacoes-ds-ponto"
                                                onClick={() =>
                                                    aoMarcarComoLida(
                                                        notificacao.id,
                                                    )
                                                }
                                                title="Marcar como lida"
                                                aria-label="Marcar como lida"
                                            />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Controle imperativo, para o HermesNotifier empurrar as notificações. */
export const criarPainelNotificacoes = (elemento: HTMLElement) => {
    const root: Root = createRoot(elemento);

    let estado: EstadoPainelNotificacoes = {
        notificacoes: [],
        aoMarcarComoLida: () => undefined,
        aoMarcarTodasComoLidas: () => undefined,
    };

    const desenhar = () => root.render(<PainelNotificacoes {...estado} />);

    desenhar();

    return {
        atualizar(parcial: Partial<EstadoPainelNotificacoes>) {
            estado = { ...estado, ...parcial };
            desenhar();
        },
        destruir() {
            root.unmount();
        },
    };
};
